import config from '@config';
import Intercom from '@platform2-lib/intercom';
import { NavItem, Select } from '@staging-design-system';
import { useShellStore } from '@store/shell';
import {
  HelpCenterBookIcon,
  HelpIcon,
  MessengerIcon,
  MessengerWithMessageIcon
} from '@ui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface Props {
  variant?: 'compact' | 'dropdown';
  onActionComplete?: () => void;
}

function Help({ variant = 'dropdown', onActionComplete }: Props) {
  const isExpanded = useShellStore((state) => state.isSidebarExpanded);
  const [isIntercomOpened, setIsIntercomOpened] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  useEffect(() => {
    const handleStateChanged = (event: Event) => {
      const { detail } = event as CustomEvent<{ isOpened: boolean }>;
      setIsIntercomOpened(Boolean(detail?.isOpened));
    };

    const handleCountChanged = (event: Event) => {
      const { detail } = event as CustomEvent<{ unreadCount: number }>;
      setHasUnreadMessages((detail?.unreadCount ?? 0) > 0);
    };

    window.addEventListener(Intercom.STATE_CHANGED_EVENT, handleStateChanged);
    window.addEventListener(
      Intercom.MESSAGE_COUNT_CHANGED_EVENT,
      handleCountChanged
    );

    return () => {
      window.removeEventListener(
        Intercom.STATE_CHANGED_EVENT,
        handleStateChanged
      );
      window.removeEventListener(
        Intercom.MESSAGE_COUNT_CHANGED_EVENT,
        handleCountChanged
      );
    };
  }, []);

  const handleHelpCenterClick = useCallback(() => {
    window.open(config.hrefs.helpCenter, '_blank', 'noreferrer=true');
    onActionComplete?.();
  }, [onActionComplete]);

  const handleLiveChatClick = useCallback(() => {
    if (isIntercomOpened) {
      Intercom.hide();
    } else {
      Intercom.show();
    }

    onActionComplete?.();
  }, [isIntercomOpened, onActionComplete]);

  const liveChatIcon = useMemo(
    () =>
      hasUnreadMessages ? <MessengerWithMessageIcon /> : <MessengerIcon />,
    [hasUnreadMessages]
  );

  const items = useMemo(
    () => [
      {
        icon: <HelpCenterBookIcon />,
        label: 'Help center',
        value: 'help_center',
        onClick: handleHelpCenterClick
      },
      {
        icon: liveChatIcon,
        label: 'Live chat',
        value: 'live_chat',
        onClick: handleLiveChatClick
      }
    ],
    [handleHelpCenterClick, handleLiveChatClick, liveChatIcon]
  );

  if (variant === 'compact') {
    return items.map((item) => (
      <NavItem
        key={item.value}
        icon={item.icon}
        label={item.label}
        showLabel
        type="button"
        onClick={item.onClick}
      />
    ));
  }

  return (
    <Select
      icon={<HelpIcon />}
      label="Help"
      options={items.map(({ icon, ...item }) => ({
        ...item,
        startIcon: icon
      }))}
      placement="top"
      showLabel={isExpanded}
    />
  );
}

export default Help;
